import React, { useState, useEffect, useCallback } from 'react';
import { jobsAPI } from '../../utils/api';
import { fetchAllPages } from '../../utils/fetchAllPages';
import InterviewFeedbackDrawer from './InterviewFeedbackDrawer';

/**
 * The interviewer's queue: what needs them now. Interviews that have ended
 * without feedback come first, then what is coming up. Built from the same
 * endpoint as the schedule view; grouping happens here.
 */
const DAY_MS = 24 * 60 * 60 * 1000;

function endOf(interview) {
  return new Date(new Date(interview.scheduledDate).getTime() + (interview.duration || 60) * 60 * 1000);
}

export function groupInterviews(interviews, now = new Date()) {
  const needsFeedback = [], upcoming = [], done = [];
  for (const i of interviews) {
    if (i.status === 'cancelled') continue;
    const ended = endOf(i) < now;
    const hasFeedback = Boolean((i.feedback && i.feedback.trim()) || i.rating);
    if (ended && !hasFeedback && i.status !== 'no-show') needsFeedback.push(i);
    else if (!ended) upcoming.push(i);
    else done.push(i);
  }
  needsFeedback.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  upcoming.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  return { needsFeedback, upcoming, done };
}

function daysAgo(date, now = new Date()) {
  const d = Math.floor((now - new Date(date)) / DAY_MS);
  if (d <= 0) return 'today';
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

function when(date) {
  return new Date(date).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// The dashboard stylesheet's .btn rules are overridden by page-level button
// styles, so the queue carries its own.
const STATUS_COLOR = { scheduled: ['#DBEAFE', '#1E40AF'], confirmed: ['#DBEAFE', '#1E40AF'], completed: ['#D1FAE5', '#065F46'], 'no-show': ['#FEE2E2', '#991B1B'], rescheduled: ['#FEF3C7', '#92400E'] };
const badgeStyle = (status) => { const [bg, fg] = STATUS_COLOR[status] || ['#F3F4F6', '#374151']; return { background: bg, color: fg, padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' }; };
const btnPrimary = { background: '#1E40AF', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { ...btnPrimary, background: '#fff', color: '#111827', border: '1px solid #D1D5DB', textDecoration: 'none', display: 'inline-block' };
const sectionTitle = { fontSize: '1rem', fontWeight: 600, margin: '1.25rem 0 0.5rem', color: '#111827' };

function InterviewQueue() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedbackInterview, setFeedbackInterview] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = await fetchAllPages(
        (pageParams) => jobsAPI.getMyInterviews({ ...pageParams }),
        (data) => data.interviews
      );
      setInterviews(all);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load your interviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { needsFeedback, upcoming, done } = groupInterviews(interviews);
  const soon = upcoming.filter(i => new Date(i.scheduledDate) - Date.now() < 7 * DAY_MS);

  const row = (i, action) => (
    <div key={i._id} className="assessment-card">
      <div className="card-header">
        <h3>{i.candidate?.name || 'Candidate'}</h3>
        <span style={badgeStyle(i.status)}>{i.status}</span>
      </div>
      <div className="card-body">
        <div className="info-row"><span className="label">Position:</span><span className="value">{i.job?.title || i.jobPostingId?.title || '-'}</span></div>
        <div className="info-row"><span className="label">When:</span><span className="value">{when(i.scheduledDate)} ({i.duration} min)</span></div>
        {i.assessment?.assigned && (
          <div className="info-row"><span className="label">Assessment:</span><span className="value">{i.assessment.completed ? 'completed' : 'assigned, not completed'}</span></div>
        )}
      </div>
      <div className="card-actions">{action}</div>
    </div>
  );

  return (
    <div className="assessments-section">
      <div className="filter-bar">
        <h2>My Queue</h2>
        <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>
          {needsFeedback.length} awaiting your feedback, {soon.length} in the next 7 days, {done.length} completed
        </span>
      </div>

      {error && <div className="error-banner"><span>{error}</span></div>}
      {loading && <p style={{ color: '#6B7280' }}>Loading...</p>}

      {!loading && (
        <>
          <h3 style={sectionTitle}>Awaiting your feedback</h3>
          <div className="assessments-grid">
            {needsFeedback.length === 0
              ? <div className="empty-state"><p>Nothing waiting on you.</p></div>
              : needsFeedback.map(i => row(i, (
                <button style={btnPrimary} onClick={() => setFeedbackInterview(i)}>
                  Submit feedback ({daysAgo(endOf(i))})
                </button>
              )))}
          </div>

          <h3 style={sectionTitle}>Coming up</h3>
          <div className="assessments-grid">
            {upcoming.length === 0
              ? <div className="empty-state"><p>No interviews scheduled.</p></div>
              : upcoming.map(i => row(i, i.meetingLink
                ? <a style={btnSecondary} href={i.meetingLink} target="_blank" rel="noopener noreferrer">Join meeting</a>
                : <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>No meeting link yet</span>))}
          </div>
        </>
      )}

      <InterviewFeedbackDrawer
        interview={feedbackInterview}
        isOpen={Boolean(feedbackInterview)}
        onClose={() => setFeedbackInterview(null)}
        onSubmitted={() => { setFeedbackInterview(null); load(); }}
      />
    </div>
  );
}

export default InterviewQueue;
